import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    n: "01",
    title: "You travel. We transcribe.",
    body: "Visa forms are written for bureaucrats, not for people with jobs, families and flights to catch. Hand us your details once - we translate them into every field, format and footnote your consulate expects.",
  },
  {
    n: "02",
    title: "Form-perfect, first time.",
    body: "A single mismatched date or missing checkbox can cost you weeks. Every Triodocz file passes a two-specialist review against the current embassy checklist before it ever reaches your inbox.",
  },
  {
    n: "03",
    title: "A human, not a portal.",
    body: "No ticket queues, no chatbots reading scripts. You get one named specialist who knows your file, answers in plain language and tells you honestly what your chances are.",
  },
];

export const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="px-6 md:px-12 py-28 md:py-36 bg-[#F3F3F1] border-y border-slate-200"
  >
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-16">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5"
          >
            About
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.1]"
          >
            The visa people for people who'd rather be{" "}
            <em className="italic text-slate-500">packing.</em>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="mt-7 text-sm text-slate-600 leading-relaxed max-w-sm"
          >
            Triodocz is a private documentation consultancy. We are the
            people who make paperwork behave.
          </motion.p>
          <motion.a
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.35 }}
            href="#contact"
            data-testid="about-contact-link"
            className="group inline-flex items-center gap-2 mt-8 text-sm font-semibold text-slate-900 border-b border-slate-900 pb-1 hover:text-slate-500 hover:border-slate-400 transition-colors duration-300"
          >
            Meet your specialist
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </motion.a>
        </div>
      </div>

      <div className="lg:col-span-7">
        {CHAPTERS.map((c, i) => (
          <motion.div
            key={c.n}
            data-testid={`about-chapter-${c.n}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: EASE }}
            className={`grid sm:grid-cols-12 gap-6 py-12 ${i > 0 ? "border-t border-slate-300/70" : ""}`}
          >
            <div className="sm:col-span-3">
              <span className="font-display text-6xl sm:text-7xl text-slate-300 leading-none">{c.n}</span>
            </div>
            <div className="sm:col-span-9">
              <h3 className="font-display text-2xl sm:text-3xl tracking-tight text-slate-900">{c.title}</h3>
              <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed max-w-lg">{c.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
